import {inject, injectable} from "inversify";
import {GroundSlot} from "@/modules/club-grounds/entity";
import {BankCategory} from "@/modules/finances/enums";
import {EventEmitter} from "@/modules/base/events";
import {BeforeGroundSlotUnlockedEvent} from "@/modules/club-grounds/events/BeforeGroundSlotUnlockedEvent";
import {AfterGroundSlotUnlockedEvent} from "@/modules/club-grounds/events/AfterGroundSlotUnlockedEvent";
import {Season} from "@/modules/season/entity";
import {BeforeGroundSlotAvailableEvent} from "@/modules/club-grounds/events/BeforeGroundSlotAvailableEvent";
import {AfterGroundSlotAvailableEvent} from "@/modules/club-grounds/events/AfterGroundSlotAvailableEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
export default class SlotUnlocker {
  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async finish(slot: GroundSlot) {
    await this.em.emitAsync(new BeforeGroundSlotAvailableEvent(slot));

    slot.available = true;
    await slot.save();

    const nextSlot = await GroundSlot
      .createQueryBuilder('slot')
      .where('slot.id = :id', {id: slot.id + 1})
      .getOne();

    if (nextSlot) {
      nextSlot.unlockable = true;
      await nextSlot.save();
    }

    await this.em.emitAsync(new AfterGroundSlotAvailableEvent(slot));
  }

  public async unlock(slot: GroundSlot) {
    await this.em.emitAsync(new BeforeGroundSlotUnlockedEvent(slot));

    const season = await Season.current();
    const duration = this.configRepository.get('clubGrounds.durations.unlock')(slot);
    const price = this.configRepository.get('clubGrounds.costs.unlock')(slot);

    await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
      price,
      {
        category: BankCategory.CLUB_GROUNDS,
        label: translator.t('unlocked_club_grounds'),
      }
    );

    slot.locked = false;
    slot.availableAt = season.currentDate.add(duration, 'days').toDate();
    await slot.save();

    await this.em.emitAsync(new AfterGroundSlotUnlockedEvent(slot));
  }
}
